import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { sendPasswordResetEmail } from '@/lib/firebase';
import {
  Sun,
  Moon,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Loader2,
} from 'lucide-react';
import { z } from 'zod';
import type { FirebaseAuthError } from '@/types/charts';

const emailSchema = z.string().email('Por favor, insira um e-mail válido');

const ForgotPassword: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { toast } = useToast();

  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (value: string) => {
    try {
      emailSchema.parse(value);
      setError('');
      return true;
    } catch (err) {
      if (err instanceof z.ZodError) {
        setError(err.errors[0].message);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      return;
    }

    setIsSubmitting(true);

    try {
      await sendPasswordResetEmail(email);
      setEmailSent(true);
      toast({
        title: 'E-mail enviado',
        description: 'Verifique sua caixa de entrada para as instruções de recuperação.',
      });
    } catch (error: unknown) {
      let errorMessage = 'Não foi possível enviar o e-mail. Tente novamente.';
      const firebaseError = error as FirebaseAuthError;

      if (firebaseError.code === 'auth/user-not-found') {
        errorMessage = 'Não encontramos uma conta com este e-mail.';
      } else if (firebaseError.code === 'auth/invalid-email') {
        errorMessage = 'Por favor, insira um e-mail válido.';
      } else if (firebaseError.code === 'auth/too-many-requests') {
        errorMessage = 'Muitas tentativas. Tente novamente mais tarde.';
      }

      toast({
        title: 'Erro',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-5 sm:p-8 relative">
      <button
        onClick={toggleTheme}
        className="absolute top-5 right-5 p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
        aria-label={theme === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
      >
        {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>

      <div className="w-full max-w-sm animate-fade-up">
        {/* Logo */}
        <div className="flex items-center gap-2.5 mb-8">
          <div className="h-7 w-7 rounded-md bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-xs font-display">P</span>
          </div>
          <span className="text-foreground font-semibold">Patri</span>
        </div>

        {emailSent ? (
          <>
            <div className="mb-8">
              <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-md bg-primary/10">
                <CheckCircle className="h-5 w-5 text-primary" />
              </div>
              <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground mb-2">
                Recuperação
              </p>
              <h1 className="font-display font-bold text-foreground leading-[1.05] tracking-tight mb-1.5 text-[2rem]">
                Verifique seu e-mail
              </h1>
              <p className="text-muted-foreground text-sm">
                Enviamos as instruções de recuperação para{' '}
                <strong className="text-foreground font-medium">{email}</strong>.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setEmailSent(false)}
              className="w-full py-2.5 px-4 rounded-md border border-input bg-background text-sm font-medium text-foreground hover:bg-muted transition-colors"
            >
              Tentar outro e-mail
            </button>

            <p className="text-center text-sm text-muted-foreground mt-6">
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-primary font-medium hover:opacity-80 transition-opacity"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Voltar para o login
              </Link>
            </p>
          </>
        ) : (
          <>
            <div className="mb-8">
              <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground mb-2">
                Recuperação
              </p>
              <h1 className="font-display font-bold text-foreground leading-[1.05] tracking-tight mb-1.5 text-[2rem]">
                Esqueceu a senha?
              </h1>
              <p className="text-muted-foreground text-sm">
                Digite seu e-mail e enviaremos instruções para redefinir sua senha.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm font-medium">
                  E-mail
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="voce@exemplo.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (e.target.value) validateEmail(e.target.value);
                  }}
                  className={error ? 'border-destructive' : ''}
                  disabled={isSubmitting}
                />
                {error && <p className="text-xs text-destructive">{error}</p>}
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !email}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Enviando...
                  </>
                ) : (
                  <>
                    <span>Enviar instruções</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </>
                )}
              </button>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-6">
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-primary font-medium hover:opacity-80 transition-opacity"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Voltar para o login
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
